import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { apartmentsApi, type Apartment, type Building } from '../../api/apartmentsApi';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import {
  parkingManagementApi,
  type MonthlyVehicle,
  type MonthlyVehicleActionLog,
  type ParkingRecordStatus,
  type VehicleCardType,
} from '../../api/parkingManagementApi';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { formatDateToDisplay, getFirstDayOfNextMonthDisplay, parseDisplayDateToIso } from '../../utils/apartmentDate';
import { MonthlyVehicleImportModal } from '../../components/apartments/MonthlyVehicleImportModal';
import { AccessCardSearchPicker } from '../../components/apartments/AccessCardSearchPicker';
import ApartmentServiceFeePaymentModal from '../../components/modals/ApartmentServiceFeePaymentModal';
import { useFunctionPermissions } from '../../hooks/useFunctionPermissions';
import ImportFileLink from '../../components/common/ImportFileLink';
import { BulkListPagination } from '../../components/bulk-fee/BulkListPagination';
import { HISTORY_PAGE_SIZE } from '../../api/paginatedHistory';
import { formatVndInput } from '../../utils/vndFormat';

const STATUS_OPTIONS: ParkingRecordStatus[] = ['OK', 'EXPIRED', 'LOCKED', 'DELETED'];
type TabKind = 'VEHICLES' | 'HISTORY';

const BUILDING_LIST_FETCH_LIMIT = 100_000;

function normalizePlateForSearch(value: string): string {
  return value.replace(/[\s\-.]/g, '').toLowerCase();
}

function matchesMonthlyVehicleSearch(vehicle: MonthlyVehicle, rawQuery: string): boolean {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return true;
  const qPlate = normalizePlateForSearch(q);
  const plate = vehicle.licensePlate?.toLowerCase() ?? '';
  const plateNorm = normalizePlateForSearch(plate);
  return (
    plate.includes(q) ||
    (qPlate.length >= 2 && plateNorm.includes(qPlate)) ||
    (vehicle.vehicleModel?.toLowerCase().includes(q) ?? false) ||
    (vehicle.phone?.toLowerCase().includes(q) ?? false) ||
    (vehicle.parkingSpotNumber?.toLowerCase().includes(q) ?? false) ||
    (vehicle.ownerName?.toLowerCase().includes(q) ?? false) ||
    (vehicle.apartment?.code?.toLowerCase().includes(q) ?? false) ||
    (vehicle.apartment?.name?.toLowerCase().includes(q) ?? false) ||
    (vehicle.accessCard?.cardNumber?.toLowerCase().includes(q) ?? false) ||
    (vehicle.accessCard?.cardCode?.toLowerCase().includes(q) ?? false) ||
    (vehicle.vehicleCardType?.name?.toLowerCase().includes(q) ?? false)
  );
}

const MonthlyVehicleManagement: React.FC = () => {
  const { t } = useLang();
  const { user, isSuperAdmin, isOperator } = useAuth();
  const { can } = useFunctionPermissions();
  const canImportVehicles = can('MONTHLY_VEHICLE_MANAGEMENT_IMPORT', 'import');
  const canBulkDelete = isSuperAdmin || isOperator;
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [activeTab, setActiveTab] = useState<TabKind>('VEHICLES');
  const [allVehicles, setAllVehicles] = useState<MonthlyVehicle[]>([]);
  const [actionLogs, setActionLogs] = useState<MonthlyVehicleActionLog[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [vehicleCardTypes, setVehicleCardTypes] = useState<VehicleCardType[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<MonthlyVehicle | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [cardLookupError, setCardLookupError] = useState<string | null>(null);
  const [accessCardId, setAccessCardId] = useState('');
  const [createCardIssuanceFee, setCreateCardIssuanceFee] = useState(true);
  const [originalAccessCardId, setOriginalAccessCardId] = useState('');
  const [createParkingFeeInvoice, setCreateParkingFeeInvoice] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentContext, setPaymentContext] = useState<{
    apartmentId: string;
    apartmentCode: string;
    invoiceIds: string[];
  } | null>(null);
  const [endOfMonthChecked, setEndOfMonthChecked] = useState(false);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [showLegacyDeleted, setShowLegacyDeleted] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ vehicleIds: string[] } | null>(null);
  const [deleteNotes, setDeleteNotes] = useState('');
  const [restoreModal, setRestoreModal] = useState<{ vehicleIds: string[] } | null>(null);
  const [restoreNotes, setRestoreNotes] = useState('');
  const [legacyDeletedCount, setLegacyDeletedCount] = useState(0);

  const [form, setForm] = useState({
    apartmentId: '',
    cardNumber: '',
    vehicleCardTypeId: '',
    licensePlate: '',
    vehicleModel: '',
    startDate: '',
    endDate: '',
    phone: '',
    ownerName: '',
    parkingSpotNumber: '',
    status: 'OK' as ParkingRecordStatus,
    notes: '',
    monthlyParkingFee: '',
  });

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setHistoryPage(1);
    setSelectedVehicleIds([]);
  }, [searchInput, activeTab, selectedBuildingId, showLegacyDeleted]);

  const filteredVehicles = useMemo(
    () => allVehicles.filter((vehicle) => matchesMonthlyVehicleSearch(vehicle, searchInput)),
    [allVehicles, searchInput],
  );

  const totalRecords = filteredVehicles.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage) || 1);

  const vehicles = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return filteredVehicles.slice(start, start + recordsPerPage);
  }, [filteredVehicles, currentPage, recordsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const loadBuildings = async () => {
    try {
      const data = await apartmentsApi.getBuildings();
      setBuildings(data);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
    } catch {
      setError(t('failedToLoad'));
    }
  };

  const loadData = useCallback(async () => {
    if (!selectedBuildingId) return;
    try {
      setLoading(true);
      setError(null);

      if (activeTab === 'HISTORY') {
        const result = await parkingManagementApi.getMonthlyVehicleActionLogs(selectedBuildingId, {
          page: historyPage,
          limit: HISTORY_PAGE_SIZE,
        });
        setActionLogs(result.data);
        setHistoryTotal(result.meta.total);
        setHistoryTotalPages(result.meta.totalPages);
        setAllVehicles([]);
        return;
      }

      const [vehicleResult, types, apts] = await Promise.all([
        parkingManagementApi.listMonthlyVehicles({
          buildingId: selectedBuildingId,
          page: 1,
          limit: BUILDING_LIST_FETCH_LIMIT,
          showLegacyDeleted,
        }),
        parkingManagementApi.listVehicleCardTypes(selectedBuildingId),
        apartmentsApi.getApartments({ buildingId: selectedBuildingId }),
      ]);
      setAllVehicles(vehicleResult.data);
      setLegacyDeletedCount(vehicleResult.meta.legacyDeletedCount ?? 0);
      setVehicleCardTypes(types);
      setApartments(apts);
      setSelectedVehicleIds((prev) =>
        prev.filter((id) => vehicleResult.data.some((vehicle) => vehicle.id === id)),
      );
    } catch (err) {
      setError(getApiErrorMessage(err, t('failedToLoad')));
      setAllVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [selectedBuildingId, activeTab, showLegacyDeleted, historyPage, t]);

  useEffect(() => {
    if (selectedBuildingId) {
      loadData();
    } else {
      setAllVehicles([]);
      setActionLogs([]);
    }
  }, [selectedBuildingId, loadData]);

  const resetForm = () => {
    setForm({
      apartmentId: '',
      cardNumber: '',
      vehicleCardTypeId: vehicleCardTypes[0]?.id ?? '',
      licensePlate: '',
      vehicleModel: '',
      startDate: '',
      endDate: '',
      phone: '',
      ownerName: '',
      parkingSpotNumber: '',
      status: 'OK',
      notes: '',
      monthlyParkingFee: '',
    });
    setEditingVehicle(null);
    setEndOfMonthChecked(false);
    setAccessCardId('');
    setCreateCardIssuanceFee(true);
    setCreateParkingFeeInvoice(true);
    setCardLookupError(null);
    setOriginalAccessCardId('');
  };

  const openCreateModal = () => {
    resetForm();
    setShowFormModal(true);
  };

  const openEditModal = (vehicle: MonthlyVehicle) => {
    setEditingVehicle(vehicle);
    const initialCardId = vehicle.accessCard?.id ?? '';
    setAccessCardId(initialCardId);
    setOriginalAccessCardId(initialCardId);
    setCreateCardIssuanceFee(true);
    setForm({
      apartmentId: vehicle.apartmentId,
      cardNumber: vehicle.accessCard?.cardNumber ?? '',
      vehicleCardTypeId: vehicle.vehicleCardTypeId,
      licensePlate: vehicle.licensePlate ?? '',
      vehicleModel: vehicle.vehicleModel ?? '',
      startDate: vehicle.startDate ? formatDateToDisplay(vehicle.startDate) : '',
      endDate: vehicle.endDate ? formatDateToDisplay(vehicle.endDate) : '',
      phone: vehicle.phone ?? '',
      ownerName: vehicle.ownerName ?? '',
      parkingSpotNumber: vehicle.parkingSpotNumber ?? '',
      status: vehicle.status,
      notes: vehicle.notes ?? '',
      monthlyParkingFee:
        vehicle.monthlyParkingFee != null && vehicle.monthlyParkingFee !== ''
          ? formatVndInput(vehicle.monthlyParkingFee)
          : '',
    });
    setShowFormModal(true);
    setEndOfMonthChecked(false);
  };

  const cardReplacementPending = Boolean(
    editingVehicle && accessCardId && accessCardId !== originalAccessCardId,
  );
  const cardClearedOnEdit = Boolean(editingVehicle && originalAccessCardId && !accessCardId);

  const parseMonthlyParkingFeeForm = (raw: string): number | null | undefined => {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    const digits = trimmed.replace(/[^\d]/g, '');
    if (!digits) return undefined;
    return Number(digits);
  };

  const formatMonthlyParkingFeeCell = (vehicle: MonthlyVehicle) => {
    if (vehicle.monthlyParkingFee == null || vehicle.monthlyParkingFee === '') {
      return t('monthlyVehicleParkingFeeByCategory');
    }
    const amount = Number(vehicle.monthlyParkingFee);
    if (Number.isNaN(amount)) return '-';
    if (amount === 0) return '0';
    return amount.toLocaleString('vi-VN');
  };

  const buildCommonSaveFields = () => ({
    buildingId: selectedBuildingId,
    apartmentId: form.apartmentId,
    cardNumber: accessCardId ? undefined : form.cardNumber || undefined,
    vehicleCardTypeId: form.vehicleCardTypeId,
    licensePlate: form.licensePlate || undefined,
    vehicleModel: form.vehicleModel || undefined,
    startDate: parseDisplayDateToIso(form.startDate),
    endDate: parseDisplayDateToIso(form.endDate),
    phone: form.phone.trim() || undefined,
    ownerName: form.ownerName.trim() || undefined,
    parkingSpotNumber: form.parkingSpotNumber.trim() || undefined,
    status: form.status,
    monthlyParkingFee: parseMonthlyParkingFeeForm(form.monthlyParkingFee),
  });

  const buildMonthlyVehicleUpdatePayload = () => {
    const { buildingId: _buildingId, startDate, endDate, monthlyParkingFee, ...rest } =
      buildCommonSaveFields();
    const payload: Parameters<typeof parkingManagementApi.updateMonthlyVehicle>[1] = {
      ...rest,
      phone: form.phone.trim() || null,
      ownerName: form.ownerName.trim() || null,
      parkingSpotNumber: form.parkingSpotNumber.trim() || null,
      accessCardId: accessCardId || null,
      vehicleModel: form.vehicleModel.trim() || null,
      notes: form.notes.trim() || null,
      createCardIssuanceFee: cardReplacementPending && createCardIssuanceFee,
    };

    if (!editingVehicle) return payload;

    const loadedStart = editingVehicle.startDate
      ? formatDateToDisplay(editingVehicle.startDate)
      : '';
    const loadedEnd = editingVehicle.endDate ? formatDateToDisplay(editingVehicle.endDate) : '';
    if (form.startDate.trim() !== loadedStart) {
      payload.startDate = startDate ?? null;
    }
    if (form.endDate.trim() !== loadedEnd) {
      payload.endDate = endDate ?? null;
    }

    const loadedFee =
      editingVehicle.monthlyParkingFee != null && editingVehicle.monthlyParkingFee !== ''
        ? formatVndInput(editingVehicle.monthlyParkingFee)
        : '';
    if (form.monthlyParkingFee.trim() !== loadedFee) {
      payload.monthlyParkingFee = monthlyParkingFee;
    }

    return payload;
  };

  const handleSave = async (openPaymentAfterSave = false) => {
    if (!selectedBuildingId || !form.apartmentId || !form.vehicleCardTypeId) return;
    if (!editingVehicle && createCardIssuanceFee && !accessCardId) {
      setError(t('monthlyVehicleCardRequiredForIssuanceFee'));
      return;
    }
    if (!editingVehicle && createParkingFeeInvoice && (!form.startDate.trim() || !form.endDate.trim())) {
      setError(t('monthlyVehicleDatesRequiredForParkingFee'));
      return;
    }
    if (cardReplacementPending && !accessCardId) {
      setError(t('monthlyVehicleCardRequiredForIssuanceFee'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      if (editingVehicle) {
        const updated = await parkingManagementApi.updateMonthlyVehicle(
          editingVehicle.id,
          buildMonthlyVehicleUpdatePayload(),
        );
        const apartment = apartments.find((apt) => apt.id === form.apartmentId);
        const invoiceIds = updated.createdInvoiceIds ?? [];

        setShowFormModal(false);
        resetForm();
        await loadData();

        if (openPaymentAfterSave && invoiceIds.length > 0 && apartment) {
          setPaymentContext({
            apartmentId: apartment.id,
            apartmentCode: apartment.code,
            invoiceIds,
          });
          setShowPaymentModal(true);
        }
        return;
      }

      const created = await parkingManagementApi.createMonthlyVehicle({
        ...buildCommonSaveFields(),
        accessCardId: accessCardId || undefined,
        vehicleModel: form.vehicleModel.trim() || undefined,
        notes: form.notes.trim() || undefined,
        createCardIssuanceFee,
        createParkingFeeInvoice,
      });

      const apartment = apartments.find((apt) => apt.id === form.apartmentId);
      const invoiceIds = created.createdInvoiceIds ?? [];

      setShowFormModal(false);
      resetForm();
      await loadData();

      if (openPaymentAfterSave && invoiceIds.length > 0 && apartment) {
        setPaymentContext({
          apartmentId: apartment.id,
          apartmentCode: apartment.code,
          invoiceIds,
        });
        setShowPaymentModal(true);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, t('saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndPay = () => handleSave(true);

  const openDeleteModal = (vehicleIds: string[]) => {
    setDeleteNotes('');
    setDeleteModal({ vehicleIds });
  };

  const closeDeleteModal = () => {
    setDeleteModal(null);
    setDeleteNotes('');
  };

  const confirmDeleteVehicles = async () => {
    if (!deleteModal || !selectedBuildingId) return;
    const { vehicleIds } = deleteModal;
    const notes = deleteNotes.trim() || undefined;

    try {
      setBulkDeleteLoading(true);
      setError(null);
      if (vehicleIds.length === 1) {
        await parkingManagementApi.removeMonthlyVehicle(vehicleIds[0], notes);
        setSelectedVehicleIds((prev) => prev.filter((id) => id !== vehicleIds[0]));
      } else {
        const result = await parkingManagementApi.bulkDeleteMonthlyVehicles(
          selectedBuildingId,
          vehicleIds,
          notes,
        );
        setSelectedVehicleIds((prev) => prev.filter((id) => !result.deletedIds.includes(id)));
        setImportResult(
          t('bulkDeleteMonthlyVehiclesResultSummary').replace(
            '{deleted}',
            result.deletedCount.toString(),
          ),
        );
      }
      closeDeleteModal();
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, t('deleteFailed')));
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const toggleVehicleSelection = (id: string) => {
    setSelectedVehicleIds((prev) =>
      prev.includes(id) ? prev.filter((vehicleId) => vehicleId !== id) : [...prev, id],
    );
  };

  const toggleSelectAllOnPage = () => {
    const pageIds = vehicles.map((vehicle) => vehicle.id);
    const allSelected =
      pageIds.length > 0 && pageIds.every((id) => selectedVehicleIds.includes(id));
    if (allSelected) {
      setSelectedVehicleIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      return;
    }
    setSelectedVehicleIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const handleBulkDelete = () => {
    if (!canBulkDelete || !selectedBuildingId || selectedVehicleIds.length === 0) return;
    openDeleteModal(selectedVehicleIds);
  };

  const openRestoreModal = (vehicleIds: string[]) => {
    setRestoreNotes('');
    setRestoreModal({ vehicleIds });
  };

  const closeRestoreModal = () => {
    setRestoreModal(null);
    setRestoreNotes('');
  };

  const confirmRestoreVehicles = async () => {
    if (!restoreModal || !selectedBuildingId) return;
    const { vehicleIds } = restoreModal;
    const notes = restoreNotes.trim() || undefined;

    try {
      setBulkDeleteLoading(true);
      setError(null);
      if (vehicleIds.length === 1) {
        await parkingManagementApi.restoreMonthlyVehicle(vehicleIds[0], notes);
        setSelectedVehicleIds((prev) => prev.filter((id) => id !== vehicleIds[0]));
        setImportResult(t('bulkRestoreMonthlyVehiclesResultSummary').replace('{restored}', '1'));
      } else {
        const result = await parkingManagementApi.bulkRestoreMonthlyVehicles(
          selectedBuildingId,
          vehicleIds,
          notes,
        );
        setSelectedVehicleIds((prev) => prev.filter((id) => !result.restoredIds.includes(id)));
        setImportResult(
          t('bulkRestoreMonthlyVehiclesResultSummary').replace(
            '{restored}',
            result.restoredCount.toString(),
          ),
        );
      }
      closeRestoreModal();
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, t('saveFailed')));
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleBulkRestore = () => {
    if (!canBulkDelete || !selectedBuildingId || selectedVehicleIds.length === 0) return;
    openRestoreModal(selectedVehicleIds);
  };

  const handleSync = async () => {
    if (!selectedBuildingId) return;
    try {
      setSyncing(true);
      await parkingManagementApi.syncQuantities(selectedBuildingId);
      setImportResult(t('parkingQuantitiesSynced'));
    } catch (err) {
      setError(getApiErrorMessage(err, t('syncFailed')));
    } finally {
      setSyncing(false);
    }
  };

  const handleImported = async (result: {
    created: number;
    updated: number;
    skipped: number;
    errors: string[];
  }) => {
    setImportResult(
      `${t('importComplete')}: ${result.created} ${t('importCreated')}, ${result.updated} ${t('updated')}, ${result.skipped} ${t('skipped')}${
        result.errors.length ? `. ${result.errors.slice(0, 5).join('; ')}` : ''
      }`,
    );
    setHistoryPage(1);
    setActiveTab('HISTORY');
  };

  const selectedBuilding = buildings.find((b) => b.id === selectedBuildingId);

  const statusLabel = (status: ParkingRecordStatus) => {
    switch (status) {
      case 'EXPIRED':
        return t('parkingStatusExpired');
      case 'LOCKED':
        return t('parkingStatusLocked');
      case 'DELETED':
        return t('parkingStatusDeleted');
      default:
        return 'OK';
    }
  };

  const getStatusBadgeClass = (status: ParkingRecordStatus) => {
    switch (status) {
      case 'OK':
        return 'bg-green-100 text-green-800';
      case 'EXPIRED':
        return 'bg-orange-100 text-orange-800';
      case 'LOCKED':
        return 'bg-yellow-100 text-yellow-800';
      case 'DELETED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatActionLogLicensePlate = (log: MonthlyVehicleActionLog) => {
    if (log.licensePlate?.trim()) {
      return log.licensePlate;
    }

    const metadata = log.metadata as
      | { deleted?: Array<{ licensePlate?: string | null }> }
      | null
      | undefined;
    const plates = metadata?.deleted
      ?.map((item) => item.licensePlate?.trim())
      .filter(Boolean);
    if (plates && plates.length > 0) {
      return plates.join(', ');
    }

    return '--';
  };

  const formatActionLogNotes = (log: MonthlyVehicleActionLog) => {
    if (log.notes?.trim()) {
      return log.notes;
    }

    const metadata = log.metadata as
      | {
          changeSummary?: string;
          changes?: string[];
          deleted?: Array<{
            apartmentCode?: string;
            licensePlate?: string | null;
            vehicleCardType?: string;
          }>;
          previous?: { cardNumber?: string | null };
          current?: { cardNumber?: string | null };
          accessCardReplaced?: boolean;
        }
      | null
      | undefined;

    if (metadata?.changeSummary?.trim()) {
      return metadata.changeSummary;
    }

    if (metadata?.changes?.length) {
      return metadata.changes.join('; ');
    }

    if (metadata?.accessCardReplaced && metadata.previous?.cardNumber && metadata.current?.cardNumber) {
      return `Số thẻ: ${metadata.previous.cardNumber} → ${metadata.current.cardNumber}`;
    }

    if (metadata?.deleted?.length) {
      return metadata.deleted
        .map((item) =>
          [item.apartmentCode, item.licensePlate, item.vehicleCardType].filter(Boolean).join(' - '),
        )
        .join('; ');
    }

    return '--';
  };

  const isRestoreActionLog = (log: MonthlyVehicleActionLog) =>
    log.action === 'UPDATE' &&
    Boolean((log.metadata as { restored?: boolean } | null | undefined)?.restored);

  const formatVehicleAction = (log: MonthlyVehicleActionLog) => {
    if (isRestoreActionLog(log)) {
      return t('cardActionRestore');
    }
    switch (log.action) {
      case 'CREATE':
        return t('cardActionCreate');
      case 'UPDATE':
        return t('cardActionUpdate');
      case 'DELETE':
        return t('cardActionDelete');
      case 'IMPORT':
        return t('cardActionImport');
      default:
        return log.action;
    }
  };

  const getVehicleActionBadgeClass = (log: MonthlyVehicleActionLog) => {
    if (isRestoreActionLog(log)) {
      return 'bg-teal-100 text-teal-800';
    }
    switch (log.action) {
      case 'CREATE':
        return 'bg-green-100 text-green-800';
      case 'UPDATE':
        return 'bg-blue-100 text-blue-800';
      case 'DELETE':
        return 'bg-red-100 text-red-800';
      case 'IMPORT':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('monthlyVehicleManagement')}</h1>
        <div className="flex flex-wrap gap-2">
          {activeTab === 'VEHICLES' && (
            <>
              {canImportVehicles && !showLegacyDeleted && (
                <button
                  onClick={() => setShowImportModal(true)}
                  disabled={!selectedBuildingId}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  {t('importExcel')}
                </button>
              )}
              <button
                onClick={() => parkingManagementApi.exportMonthlyVehicles(selectedBuildingId)}
                disabled={!selectedBuildingId}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {t('exportExcel')}
              </button>
              <button
                onClick={handleSync}
                disabled={!selectedBuildingId || syncing || showLegacyDeleted}
                title={t('syncParkingQuantitiesHint')}
                className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {syncing ? t('syncing') : t('syncParkingQuantities')}
              </button>
              <button
                onClick={openCreateModal}
                disabled={!selectedBuildingId || showLegacyDeleted}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {t('addMonthlyVehicle')}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">{error}</div>
      )}
      {selectedBuildingId && !showLegacyDeleted && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded mb-6 text-sm">
          {t('monthlyVehicleAutoSyncNote')}
        </div>
      )}
      {selectedBuildingId && showLegacyDeleted && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded mb-6 text-sm">
          {t('legacyDeletedViewNote')}
        </div>
      )}
      {importResult && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
          {importResult}
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        {shouldShowBuildingSelector(buildings) && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('selectBuilding')}</label>
            <select
              value={selectedBuildingId}
              onChange={(e) => {
                selectBuilding(user?.id, e.target.value, setSelectedBuildingId);
                setCurrentPage(1);
                setSearchInput('');
                setShowLegacyDeleted(false);
              }}
                disabled={!needsBuildingSelection(buildings)}
              className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">{t('selectBuildingPlaceholder')}</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      <div className="flex border-b mb-6">
        <button
          onClick={() => setActiveTab('VEHICLES')}
          className={`px-6 py-3 font-medium border-b-2 ${
            activeTab === 'VEHICLES'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('monthlyVehicleManagement')}
        </button>
        <button
          onClick={() => setActiveTab('HISTORY')}
          className={`px-6 py-3 font-medium border-b-2 ${
            activeTab === 'HISTORY'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('cardActionHistory')}
        </button>
      </div>

      {!selectedBuildingId ? (
        <div className="text-center py-12 text-gray-500">{t('pleaseSelectBuilding')}</div>
      ) : loading ? (
        <div className="text-center py-12">{t('loading')}</div>
      ) : activeTab === 'HISTORY' ? (
        <div className="overflow-x-auto bg-white rounded-lg shadow border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('exportTime')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('actions')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('licensePlate')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('notes')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('importHistoryFileName')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('cardActionStaff')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {actionLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    {new Date(log.createdAt).toLocaleString('vi-VN')}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getVehicleActionBadgeClass(log)}`}
                    >
                      {formatVehicleAction(log)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{formatActionLogLicensePlate(log)}</td>
                  <td className="px-4 py-3 text-sm max-w-md whitespace-pre-wrap break-words">
                    {formatActionLogNotes(log)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <ImportFileLink fileName={log.fileName} fileUrl={log.fileUrl} />
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {log.creator?.name || log.creator?.email || '--'}
                  </td>
                </tr>
              ))}
              {actionLogs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    {t('noData')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <BulkListPagination
            page={historyPage}
            totalPages={historyTotalPages}
            total={historyTotal}
            onPageChange={setHistoryPage}
            t={t}
          />
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('searchMonthlyVehiclesPlaceholder')}
                className="max-w-md w-full border border-gray-300 rounded-lg px-4 py-2"
              />
              <div className="flex flex-wrap items-center gap-3">
                {canBulkDelete && selectedVehicleIds.length > 0 && (
                  <>
                    {showLegacyDeleted && (
                      <button
                        type="button"
                        onClick={handleBulkRestore}
                        disabled={bulkDeleteLoading}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                      >
                        {bulkDeleteLoading
                          ? t('processing')
                          : `${t('restoreSelectedMonthlyVehicles')} (${selectedVehicleIds.length})`}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleBulkDelete}
                      disabled={bulkDeleteLoading}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {bulkDeleteLoading
                        ? t('processing')
                        : `${showLegacyDeleted ? t('purgeSelectedLegacyVehicles') : t('deleteSelectedMonthlyVehicles')} (${selectedVehicleIds.length})`}
                    </button>
                  </>
                )}
                <div className="text-sm text-gray-600">
                  {t('totalRecords')}: {totalRecords}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showLegacyDeleted}
                  onChange={(e) => {
                    setShowLegacyDeleted(e.target.checked);
                    setCurrentPage(1);
                    setSelectedVehicleIds([]);
                  }}
                />
                <span>{t('showLegacyDeletedVehicles')}</span>
              </label>
              {legacyDeletedCount > 0 && (
                <span className="text-amber-700">
                  {t('legacyDeletedCountLabel')}: {legacyDeletedCount}
                </span>
              )}
              <span className="text-gray-500">{t('showLegacyDeletedVehiclesHint')}</span>
            </div>
          </div>

          <div className="overflow-x-auto bg-white rounded-lg shadow border">
            <table className="min-w-[1280px] w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {canBulkDelete && (
                    <th className="w-10 px-2 py-2 text-left">
                      <input
                        type="checkbox"
                        checked={
                          vehicles.length > 0
                          && vehicles.every((vehicle) => selectedVehicleIds.includes(vehicle.id))
                        }
                        onChange={toggleSelectAllOnPage}
                        aria-label={t('selectAllOnPage')}
                      />
                    </th>
                  )}
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('apartmentCode')}
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('cardNumber')}
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('cardType')}
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('licensePlate')}
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('monthlyVehicleParkingFee')}
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('vehicleModel')}
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('startDate')}
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('endDate')}
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('registrationDate')}
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('phone')}
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('vehicleOwner')}
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('parkingSpot')}
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('status')}
                  </th>
                  <th className="min-w-[9rem] px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {t('notes')}
                  </th>
                  <th className="w-24 px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {vehicles.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50 align-middle">
                    {canBulkDelete && (
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={selectedVehicleIds.includes(v.id)}
                          onChange={() => toggleVehicleSelection(v.id)}
                          aria-label={`${t('select')} ${v.licensePlate ?? v.id}`}
                        />
                      </td>
                    )}
                    <td className="px-2 py-2 whitespace-nowrap font-medium text-gray-900">
                      {v.apartment?.code ? (
                        v.apartment.id || v.apartmentId ? (
                          <Link
                            to={`/apartments/${v.apartment.id ?? v.apartmentId}/details`}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                            title={v.apartment.name?.trim() || v.apartment.code}
                          >
                            {v.apartment.code}
                          </Link>
                        ) : (
                          v.apartment.code
                        )
                      ) : (
                        '-'
                      )}
                    </td>
                    <td
                      className="px-2 py-2 max-w-[6.5rem] truncate"
                      title={v.accessCard?.cardNumber ?? undefined}
                    >
                      {v.accessCard?.cardNumber ?? '-'}
                    </td>
                    <td
                      className="px-2 py-2 max-w-[5.5rem] whitespace-nowrap truncate"
                      title={v.vehicleCardType?.name}
                    >
                      {v.vehicleCardType?.name}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">{v.licensePlate ?? '-'}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right tabular-nums">
                      {formatMonthlyParkingFeeCell(v)}
                    </td>
                    <td
                      className="px-2 py-2 max-w-[6.5rem] truncate"
                      title={v.vehicleModel ?? undefined}
                    >
                      {v.vehicleModel ?? '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {v.startDate ? formatDateToDisplay(v.startDate) : '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {v.endDate ? formatDateToDisplay(v.endDate) : '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {v.registrationDate ? formatDateToDisplay(v.registrationDate) : '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">{v.phone ?? '-'}</td>
                    <td
                      className="px-2 py-2 max-w-[7.5rem] truncate"
                      title={v.ownerName ?? undefined}
                    >
                      {v.ownerName ?? '-'}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">{v.parkingSpotNumber ?? '-'}</td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <span
                        className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded ${getStatusBadgeClass(v.status)}`}
                      >
                        {statusLabel(v.status)}
                      </span>
                    </td>
                    <td className="px-2 py-2 min-w-[9rem] max-w-[14rem] align-top">
                      <div
                        className="line-clamp-2 break-words text-gray-600"
                        title={v.notes?.trim() ? v.notes : undefined}
                      >
                        {v.notes?.trim() ? v.notes : '-'}
                      </div>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {!showLegacyDeleted && (
                          <button
                            onClick={() => openEditModal(v)}
                            className="text-blue-600 hover:underline"
                          >
                            {t('edit')}
                          </button>
                        )}
                        {showLegacyDeleted && (
                          <button
                            onClick={() => openRestoreModal([v.id])}
                            className="text-teal-700 hover:underline"
                          >
                            {t('restoreMonthlyVehicle')}
                          </button>
                        )}
                        <button
                          onClick={() => openDeleteModal([v.id])}
                          className="text-red-600 hover:underline"
                        >
                          {showLegacyDeleted ? t('purgeLegacyVehicle') : t('delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {vehicles.length === 0 && (
                  <tr>
                    <td colSpan={canBulkDelete ? 16 : 15} className="px-4 py-8 text-center text-gray-500">
                      {t('noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                {t('previous')}
              </button>
              <span className="px-3 py-1">
                {currentPage} / {totalPages}
              </span>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                {t('next')}
              </button>
            </div>
          )}
        </>
      )}

      {showFormModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">
              {editingVehicle ? t('editMonthlyVehicle') : t('addMonthlyVehicle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('apartment')}</label>
                <select
                  value={form.apartmentId}
                  onChange={(e) => setForm({ ...form, apartmentId: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">{t('selectApartment')}</option>
                  {apartments.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <AccessCardSearchPicker
                  buildingId={selectedBuildingId}
                  kind="VEHICLE"
                  purpose="MONTHLY_VEHICLE"
                  excludeMonthlyVehicleId={editingVehicle?.id}
                  value={accessCardId}
                  currentCard={
                    editingVehicle?.accessCard
                      ? {
                          id: editingVehicle.accessCard.id,
                          cardNumber: editingVehicle.accessCard.cardNumber,
                          cardCode: editingVehicle.accessCard.cardCode ?? null,
                        }
                      : null
                  }
                  label={t('cardNumber')}
                  placeholder={t('cardNumberSearchPlaceholder')}
                  hideHint
                  onChange={(cardId, card) => {
                    setAccessCardId(cardId);
                    setCardLookupError(null);
                    setForm((prev) => ({
                      ...prev,
                      cardNumber: card?.cardNumber ?? '',
                      vehicleCardTypeId:
                        card?.vehicleCardTypeId ??
                        card?.vehicleCardType?.id ??
                        prev.vehicleCardTypeId,
                    }));
                  }}
                />
                {cardClearedOnEdit && (
                  <p className="text-xs text-gray-600 mt-1">
                    {t('monthlyVehicleCardClearedHint')}
                  </p>
                )}
                {cardReplacementPending && (
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer mt-2">
                    <input
                      type="checkbox"
                      checked={createCardIssuanceFee}
                      onChange={(e) => setCreateCardIssuanceFee(e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 text-blue-600"
                    />
                    <span>{t('createVehicleCardIssuanceFee')}</span>
                  </label>
                )}
                {cardLookupError && (
                  <p className="text-xs text-amber-600 mt-1">{cardLookupError}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('cardType')}</label>
                <select
                  value={form.vehicleCardTypeId}
                  onChange={(e) => setForm({ ...form, vehicleCardTypeId: e.target.value })}
                  className="w-full border rounded px-3 py-2 bg-gray-50"
                >
                  {vehicleCardTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('licensePlate')}</label>
                <input
                  value={form.licensePlate}
                  onChange={(e) => setForm({ ...form, licensePlate: e.target.value })}
                  onBlur={(e) => {
                    const trimmed = e.target.value.normalize('NFKC').trim();
                    if (trimmed !== form.licensePlate) {
                      setForm((prev) => ({ ...prev, licensePlate: trimmed }));
                    }
                  }}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('vehicleModel')}</label>
                <input
                  value={form.vehicleModel}
                  onChange={(e) => setForm({ ...form, vehicleModel: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder={t('vehicleModelPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('startDate')}</label>
                <input
                  type="text"
                  placeholder="dd/mm/yyyy"
                  value={form.startDate}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, startDate: e.target.value }));
                  }}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium">{t('endDate')}</label>
                  <label className="inline-flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={endOfMonthChecked}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setEndOfMonthChecked(checked);
                        if (checked) {
                          setForm((prev) => ({
                            ...prev,
                            endDate: getFirstDayOfNextMonthDisplay(prev.startDate || undefined),
                          }));
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    {t('endOfMonth')}
                  </label>
                </div>
                <input
                  type="text"
                  placeholder="dd/mm/yyyy"
                  value={form.endDate}
                  onChange={(e) => {
                    setEndOfMonthChecked(false);
                    setForm((prev) => ({ ...prev, endDate: e.target.value }));
                  }}
                  className="w-full border rounded px-3 py-2"
                />
                <p className="mt-1 text-xs text-gray-500">{t('parkingExpiryHint')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('phone')}</label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('vehicleOwner')}</label>
                <input
                  value={form.ownerName}
                  onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('parkingSpot')}</label>
                <input
                  value={form.parkingSpotNumber}
                  onChange={(e) => setForm({ ...form, parkingSpotNumber: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('monthlyVehicleParkingFee')}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.monthlyParkingFee}
                  onChange={(e) =>
                    setForm({ ...form, monthlyParkingFee: formatVndInput(e.target.value) })
                  }
                  placeholder={t('monthlyVehicleParkingFeeByCategory')}
                  className="w-full border rounded px-3 py-2"
                />
                <p className="mt-1 text-xs text-gray-500">{t('monthlyVehicleParkingFeeHint')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('status')}</label>
                <select
                  value={form.status}
                  onChange={(e) =>
                    setForm({ ...form, status: e.target.value as ParkingRecordStatus })
                  }
                  className="w-full border rounded px-3 py-2"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {statusLabel(s)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">{t('notes')}</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows={2}
                />
              </div>
              {!editingVehicle && (
                <div className="md:col-span-2 space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createCardIssuanceFee}
                      onChange={(e) => setCreateCardIssuanceFee(e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 text-blue-600"
                    />
                    <span>{t('createVehicleCardIssuanceFee')}</span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createParkingFeeInvoice}
                      disabled={parseMonthlyParkingFeeForm(form.monthlyParkingFee) === 0}
                      onChange={(e) => setCreateParkingFeeInvoice(e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 text-blue-600"
                    />
                    <span>{t('createParkingFeeInvoice')}</span>
                  </label>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowFormModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border rounded"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => handleSave(false)}
                disabled={saving || !form.apartmentId || !form.vehicleCardTypeId}
                className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50"
              >
                {saving ? t('saving') : t('save')}
              </button>
              {(!editingVehicle ||
                (cardReplacementPending && createCardIssuanceFee)) && (
                <button
                  onClick={handleSaveAndPay}
                  disabled={
                    saving ||
                    !form.apartmentId ||
                    !form.vehicleCardTypeId ||
                    (!editingVehicle && !createCardIssuanceFee && !createParkingFeeInvoice) ||
                    (Boolean(editingVehicle) &&
                      (!cardReplacementPending || !accessCardId || !createCardIssuanceFee))
                  }
                  className="px-4 py-2 bg-cyan-600 text-white rounded disabled:opacity-50"
                >
                  {saving ? t('saving') : t('saveAndPay')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {restoreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {restoreModal.vehicleIds.length === 1
                ? t('monthlyVehicleRestoreModalTitle')
                : t('monthlyVehicleBulkRestoreModalTitle').replace(
                    '{count}',
                    restoreModal.vehicleIds.length.toString(),
                  )}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {restoreModal.vehicleIds.length === 1
                ? t('confirmRestoreMonthlyVehicle')
                : t('confirmBulkRestoreMonthlyVehicles').replace(
                    '{count}',
                    restoreModal.vehicleIds.length.toString(),
                  )}
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('notes')}</label>
            <textarea
              value={restoreNotes}
              onChange={(e) => setRestoreNotes(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder={t('monthlyVehicleRestoreNotesHint')}
            />
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={closeRestoreModal}
                disabled={bulkDeleteLoading}
                className="px-4 py-2 border rounded text-gray-700"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={confirmRestoreVehicles}
                disabled={bulkDeleteLoading}
                className="px-4 py-2 bg-teal-600 text-white rounded disabled:opacity-50"
              >
                {bulkDeleteLoading ? t('processing') : t('restoreMonthlyVehicle')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              {deleteModal.vehicleIds.length === 1
                ? t('monthlyVehicleDeleteModalTitle')
                : t('monthlyVehicleBulkDeleteModalTitle').replace(
                    '{count}',
                    deleteModal.vehicleIds.length.toString(),
                  )}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {showLegacyDeleted && deleteModal.vehicleIds.length === 1
                ? t('confirmPurgeLegacyVehicle')
                : showLegacyDeleted
                  ? t('confirmBulkPurgeLegacyVehicles').replace(
                      '{count}',
                      deleteModal.vehicleIds.length.toString(),
                    )
                  : deleteModal.vehicleIds.length === 1
                    ? t('confirmDeleteMonthlyVehicle')
                    : t('confirmBulkDeleteMonthlyVehicles').replace(
                        '{count}',
                        deleteModal.vehicleIds.length.toString(),
                      )}
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('notes')}</label>
            <textarea
              value={deleteNotes}
              onChange={(e) => setDeleteNotes(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder={t('monthlyVehicleDeleteNotesHint')}
            />
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={bulkDeleteLoading}
                className="px-4 py-2 border rounded text-gray-700"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={confirmDeleteVehicles}
                disabled={bulkDeleteLoading}
                className="px-4 py-2 bg-red-600 text-white rounded disabled:opacity-50"
              >
                {bulkDeleteLoading ? t('processing') : t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      <MonthlyVehicleImportModal
        isOpen={showImportModal}
        buildingId={selectedBuildingId}
        buildingName={selectedBuilding?.name ?? ''}
        t={t}
        onClose={() => setShowImportModal(false)}
        onImported={handleImported}
      />

      {paymentContext && (
        <ApartmentServiceFeePaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentContext(null);
            loadData();
          }}
          apartmentId={paymentContext.apartmentId}
          apartmentCode={paymentContext.apartmentCode}
          initialSelectedFeeIds={paymentContext.invoiceIds}
          onSuccess={() => {
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default MonthlyVehicleManagement;
